export const getSentryClient = async (config, { nuxtApp, onNuxtReady }) => {
    let SentrySdk;
    let delayedGlobalErrors = [];
    let delayedUnhandledRejections = [];
    let delayedCalls = [];
    const sentryHandler = new Proxy({}, {
        get(_target, method) {
            return (...params) => {
                if (SentrySdk && !SentrySdk[method]) {
                    console.warn(`SentrySdk method ${method} is not supported`);
                    return;
                }
                if (SentrySdk) {
                    // eslint-disable-next-line prefer-spread
                    SentrySdk[method].apply(SentrySdk, params);
                    return;
                }
                delayedCalls.push({ method, params });
            };
        }
    });
    if (config.disabled) {
        console.info('Sentry is disabled, skipping Sentry initialization');
        return { sentry: sentryHandler };
    }
    if (!config.dsn) {
        console.warn('Sentry DSN not set, skipping Sentry initialization');
        return { sentry: sentryHandler };
    }
    const delayGlobalError = event => {
        var _a;
        // Prevent sending this global error to sentry.
        // This error is a part of the login flow (see auth.ts middleware)
        // Eg. reduce sentry noice https://sentry.hokify.com/organizations/hokify/issues/741103/events/262af22e8ccf4ba2b0284db85fc240c7/
        if ((event === null || event === void 0 ? void 0 : event.message) === 'Login notwendig' || ((_a = event === null || event === void 0 ? void 0 : event.error) === null || _a === void 0 ? void 0 : _a.message) === 'Login notwendig') {
            return;
        }
        delayedGlobalErrors.push([
            event.message,
            event.filename,
            event.lineno,
            event.colno,
            event.error
        ]);
    };
    const delayUnhandledRejection = event => {
        var _a;
        // Prevent sending this global error to sentry.
        // This error is a part of the login flow (see auth.ts middleware)
        // Eg. reduce sentry noice https://sentry.hokify.com/organizations/hokify/issues/741103/events/262af22e8ccf4ba2b0284db85fc240c7/
        if ((event === null || event === void 0 ? void 0 : event.message) === 'Login notwendig' || ((_a = event === null || event === void 0 ? void 0 : event.error) === null || _a === void 0 ? void 0 : _a.message) === 'Login notwendig') {
            return;
        }
        if ('reason' in event && event.reason) {
            event = event.reason;
        }
        else if ('detail' in event &&
            event.detail &&
            'reason' in event.detail &&
            event.detail.reason) {
            event = event.detail.reason;
        }
        delayedUnhandledRejections.push(event);
    };
    window.addEventListener('error', delayGlobalError);
    window.addEventListener('unhandledrejection', delayUnhandledRejection);
    const sentryInitialized = async (Sentry) => {
        window.removeEventListener('error', delayGlobalError);
        window.removeEventListener('unhandledrejection', delayUnhandledRejection);
        if (delayedGlobalErrors.length) {
            if (window.onerror) {
                console.info('Reposting global errors after Sentry has loaded');
                for (const errorArgs of delayedGlobalErrors) {
                    // eslint-disable-next-line prefer-spread
                    window.onerror.apply(window, errorArgs);
                }
            }
            delayedGlobalErrors = [];
        }
        if (delayedUnhandledRejections.length) {
            if (window.onunhandledrejection) {
                console.info('Reposting unhandled promise rejection errors after Sentry has loaded');
                for (const reason of delayedUnhandledRejections) {
                    window.onunhandledrejection(reason);
                }
            }
            delayedUnhandledRejections = [];
        }
        delayedCalls.forEach(({ method, params }) => {
            // eslint-disable-next-line prefer-spread
            Sentry[method].apply(Sentry, params);
        });
        delayedCalls = [];
    };
    const initSentry = async () => {
        const Sentry = await import('@sentry/vue');
        Sentry.init({
            app: nuxtApp.vueApp,
            dsn: config.dsn,
            environment: config.environment,
            release: config.release,
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: config.tracesSampleRate,
            tracePropagationTargets: config.tracePropagationTargets
        });
        SentrySdk = Sentry;
        await sentryInitialized(Sentry);
    };
    if (config.lazy) {
        onNuxtReady(async () => {
            await initSentry();
        });
    }
    else {
        await initSentry();
    }
    return { sentry: sentryHandler };
};
//# sourceMappingURL=client.js.map